import {
  amountUtils,
  dateUtils,
  type Except,
  translationUtils,
} from '@festina-life/core'
import { FormikMultiSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import get from 'lodash-es/get'
import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'

import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isBooleanVFrame,
  isDateVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
  isEnumVFrame,
  isFixedVFrame,
  isFractionVFrame,
  isStringVFrame,
} from '../../forms/yup/validation/types'
import { useFieldVFrame } from '../useFieldVFrame'

type Props<T> = Except<
  ComponentProps<typeof FormikMultiSelect<T>>,
  'isRequired' | 'options'
> & { options?: T[] }

export const LifeMultiSelect = <T,>({
  name,
  options,
  ...restProps
}: Props<T>) => {
  const translate = useTranslate()
  const { values } = useLifeFormikContext()
  const { locale } = useIntl()
  const frameStatus = useFieldVFrame(name)

  if (isDontUseVFrame(frameStatus) && !isEnumeratedVFrame(frameStatus)) {
    return null
  }

  const value = get(values, name as string)
  const fixed = isFixedVFrame(frameStatus)
  const isRequired = isFieldRequired(frameStatus)

  let frameOptions: T[] = []
  let renderFn = restProps.renderFn

  if (isEnumeratedVFrame(frameStatus)) {
    frameOptions = [...(frameStatus.values ?? [])] as T[]

    if (isBooleanVFrame(frameStatus)) {
      renderFn = (x: T) => translationUtils.translateBoolean(!!x, translate)
    } else if (isFractionVFrame(frameStatus)) {
      frameOptions.sort()
      renderFn = (x: T) => amountUtils.formatPercentWithDecimals(x as number)
    } else if (isDateVFrame(frameStatus)) {
      frameOptions.sort()
      renderFn = (x: T) => dateUtils.dateAsStr(x as Date)
    } else if (isStringVFrame(frameStatus) || isEnumVFrame(frameStatus)) {
      frameOptions.sort((a, b) => {
        const renderedA = renderFn?.(a)
        const renderedB = renderFn?.(b)

        return typeof renderedA === 'string' && typeof renderedB === 'string'
          ? renderedA.localeCompare(renderedB, locale)
          : (a as string).localeCompare(b as string, locale)
      })
    }
  } else if (isBooleanVFrame(frameStatus)) {
    frameOptions = [true, false] as T[]
    renderFn = (x: T) => translationUtils.translateBoolean(!!x, translate)
  }

  return (
    <FormikMultiSelect
      name={name}
      editable={!fixed}
      value={fixed ? frameStatus.value : value}
      isRequired={isRequired}
      options={options ?? frameOptions}
      renderFn={renderFn}
      hasRightColumnAlways
      {...restProps}
    />
  )
}
